import VisualizadorArquivos, {
  isArquivoVisualizavel
} from 'portaltransparencia-common/components/visualizador-arquivos/VisualizadorArquivos.react.js';
import React, { useCallback, useMemo, useRef, useState } from 'react';

import Moment from 'moment';
import Modal from 'portaltransparencia-common/components/Modal.react.js';
import Panel from 'portaltransparencia-common/components/Panel.react.js';
import TableActions from 'portaltransparencia-common/components/TableActions.react.js';
import TableHeader from 'portaltransparencia-common/components/TableHeader.react.js';
import DateUtils from 'portaltransparencia-common/libs/DateUtils.js';
import NumberUtils from 'portaltransparencia-common/libs/NumberUtils';
import PropTypes from 'prop-types';
import ArquivoColuna from './ArquivoColuna.react.js';

const getNomeArquivo = arquivo => arquivo.get('nomeArquivo');

const getDataArquivo = arquivo =>
  arquivo.get('dataPublicacao') || arquivo.get('data');

const getColunasExtras = children =>
  React.Children.toArray(children)
    .filter(child => child.props && child.props.valor)
    .map(child => child.props);

const ordenarPorData = (arquivos, sortByDate) => {
  let ordenados = arquivos.sort((a, b) => {
    let dataA = getDataArquivo(a);
    let dataB = getDataArquivo(b);

    if (!dataA || !dataB) {
      return !dataA && !dataB ? 0 : dataA ? 1 : -1;
    }

    dataA = Moment(dataA);
    dataB = Moment(dataB);

    return dataA.isSame(dataB) ? 0 : dataA.isBefore(dataB) ? -1 : 1;
  });

  if (typeof sortByDate === 'string' && sortByDate.toUpperCase() === 'DESC') {
    ordenados = ordenados.reverse();
  }

  return ordenados;
};

export const Arquivo = ({
  arquivos,
  sortByDate,
  onDownload,
  getLegendaArquivo,
  emptyText,
  tituloVisualizacao,
  labelVisualizacao,
  children
}) => {
  const [visualizando, setVisualizando] = useState(false);
  const urlsPorArquivo = useRef(new Map());

  const colunasExtras = useMemo(() => getColunasExtras(children), [children]);

  const arquivosTratados = useMemo(() => {
    if (!arquivos) {
      return arquivos;
    }

    return sortByDate ? ordenarPorData(arquivos, sortByDate) : arquivos;
  }, [arquivos, sortByDate]);

  const getUrlArquivo = useCallback(
    arquivo => {
      if (!onDownload) {
        return undefined;
      }

      if (!urlsPorArquivo.current.has(arquivo)) {
        const acao = onDownload(arquivo, true);

        urlsPorArquivo.current.set(arquivo, acao && acao.url);
      }

      return urlsPorArquivo.current.get(arquivo);
    },
    [onDownload]
  );

  const hasArquivoComNome = useMemo(
    () =>
      !!arquivosTratados &&
      arquivosTratados.some(arquivo => getNomeArquivo(arquivo)),
    [arquivosTratados]
  );

  const possuiArquivoVisualizavel = useMemo(
    () =>
      !!arquivosTratados &&
      arquivosTratados.some(
        arquivo =>
          isArquivoVisualizavel(getNomeArquivo(arquivo)) &&
          !!getUrlArquivo(arquivo)
      ),
    [arquivosTratados, getUrlArquivo]
  );

  const hasDataPublicacao = useMemo(
    () =>
      !!arquivosTratados &&
      arquivosTratados.some(arquivo => getDataArquivo(arquivo)),
    [arquivosTratados]
  );

  const downloadArquivo = arquivo => () => onDownload(arquivo);

  const renderArquivo = (arquivo, index) => {
    const tamanhoArquivo = NumberUtils.formatBytes(
      arquivo.get('tamanhoArquivo')
    );
    const id = arquivo.get('id') || arquivo.get('idArquivo');
    const key = `${id}_${index}`;

    return (
      <TableActions
        key={key}
        data={arquivo}
        onDownload={onDownload && id ? downloadArquivo(arquivo) : undefined}
      >
        {hasArquivoComNome && (
          <td id="nomeArquivo">{`${
            getNomeArquivo(arquivo) ?? 'Sem arquivo'
          } ${tamanhoArquivo}`}</td>
        )}
        {hasDataPublicacao && (
          <td id="dataArquivo">
            {DateUtils.formatDateTimeShort(getDataArquivo(arquivo))}
          </td>
        )}
        {colunasExtras.map((coluna, indiceColuna) => (
          <td key={coluna.id || indiceColuna} id={coluna.id}>
            {coluna.valor(arquivo)}
          </td>
        ))}
      </TableActions>
    );
  };

  if (!arquivosTratados) {
    return <div className="info">{emptyText}</div>;
  }

  return (
    <>
      {visualizando && (
        <Modal onClose={() => setVisualizando(false)} paddingTop="2vh">
          <Panel
            title={tituloVisualizacao}
            expansible={false}
            containerStyle={{
              height: '95vh',
              display: 'flex',
              flexDirection: 'column'
            }}
            bodyStyle={{ flex: 1, minHeight: 0, marginBottom: 0 }}
          >
            <VisualizadorArquivos
              arquivos={arquivosTratados}
              getNomeArquivo={getNomeArquivo}
              getLegendaArquivo={getLegendaArquivo}
              keyExtractor={(arquivo, index) =>
                `${arquivo.get('id') || arquivo.get('idArquivo')}_${index}`
              }
              getUrlArquivo={getUrlArquivo}
              permiteDownload={!!onDownload}
              emptyText={emptyText}
            />
          </Panel>
        </Modal>
      )}
      <div className="panel-table-outer">
        {possuiArquivoVisualizavel && (
          <button
            type="button"
            className="btn module-color"
            id="botao-visualizar-arquivos"
            onClick={() => setVisualizando(true)}
            style={{ margin: '1rem' }}
          >
            <i className="fa fa-eye" /> {labelVisualizacao}
          </button>
        )}
        <table className="panel-table striped fancy">
          <TableHeader empty={arquivosTratados.isEmpty()} text={emptyText}>
            <tr>
              {hasArquivoComNome && <th>Nome do Arquivo / Descrição</th>}
              {hasDataPublicacao && <th>Data de Publicação</th>}
              {colunasExtras.map((coluna, index) => (
                <th key={coluna.id || index}>{coluna.titulo}</th>
              ))}
              <th />
            </tr>
          </TableHeader>
          <tbody>{arquivosTratados.map(renderArquivo)}</tbody>
        </table>
      </div>
    </>
  );
};

Arquivo.propTypes = {
  arquivos: PropTypes.object,
  sortByDate: PropTypes.any,
  onDownload: PropTypes.func,
  getLegendaArquivo: PropTypes.func,
  emptyText: PropTypes.string,
  tituloVisualizacao: PropTypes.string,
  labelVisualizacao: PropTypes.string,
  children: PropTypes.node
};

Arquivo.defaultProps = {
  emptyText: 'Não há arquivos.',
  tituloVisualizacao: 'Visualizador de documentos',
  labelVisualizacao: 'Visualizar Documentos'
};

Arquivo.Coluna = ArquivoColuna;

export default Arquivo;
